import { authedFetch } from '../utils/shopifyFetch'
import { useState, useEffect } from 'react'
import { clean, checkAll, FIELDS, checkSavedLocation } from '../lib/validate'
import { redirectExternal, getHost } from '../utils/shopifyBridge'
// The one ladder, shared with the pricing page and asserted against
// billing.js by the backend tests.
//
// This screen used to carry its own: $9 / $29 / $59 / $99 with yearly
// prices, four tiers, none of which the subscribe route could charge. It
// was shown to every seller during install, which is the worst place in
// the app for a wrong price, and it had drifted because nothing connected
// it to anything.
import { SHOPIFY_PLANS, EBAY_MIN_PRODUCTS, EBAY_MAX_PRODUCTS,
         EBAY_SETUP_PER_PRODUCT, tierFor, setupFee } from '../lib/plans'
import { SYNC_SOURCES } from '../lib/syncSource'

// The two sides a seller can work from. Named the way they will think of it —
// where they already keep their catalogue — rather than by sync direction.
// The title and the one-line description come from the shared table, which
// the dashboard badge and the settings headings also read — the direction a
// seller picks here is named in three places, and three hand-written copies
// of "Shopify → eBay" is three chances for them to drift apart. The extra
// sentence under each option is only asked for here, so it stays here.
const SOURCES = [
  {
    ...SYNC_SOURCES.shopify,
    title: SYNC_SOURCES.shopify.label,
    detail: 'Listings already on eBay are linked to the matching Shopify product rather than ' +
            'creating a second copy of it.',
  },
  {
    ...SYNC_SOURCES.ebay,
    title: SYNC_SOURCES.ebay.label,
    detail: 'A listing on eBay with nothing matching it in Shopify is created there for you.',
  },
]

/**
 * The hub a new install gets, which is no longer a question.
 *
 * Mirrors backend/lib/newInstallHub.js — and the backend is what decides; this
 * only keeps the screen from offering a card the server would refuse.
 *
 * SOURCES above is left whole rather than trimmed to one entry. The Shopify-hub
 * copy still has to exist for the shops running on it, which Settings and the
 * dashboard both render, and deleting it here to show one card would mean
 * writing it again the first time support unlocks a shop.
 */
const NEW_INSTALL_HUB = 'ebay'
const OFFERED_SOURCES = SOURCES.filter(s => s.id === NEW_INSTALL_HUB)

// Plain glyphs rather than emoji. Emoji render differently on every platform,
// carry a cartoon register a seller is trusting with their catalogue, and sit
// oddly beside the mark set the rest of the app already uses.
const PERMS = [
  { mark: '◫', title: 'Listings',            sub: 'Create and update your eBay listings' },
  { mark: '◳', title: 'Photos',              sub: 'Upload product images to your listings' },
  { mark: '◆', title: 'Inventory and pricing', sub: 'Keep stock and prices aligned across both stores' },
  { mark: '⬓', title: 'Orders',              sub: 'Bring eBay orders in and track fulfilment' },
]


/**
 * One address box that cleans itself as it is typed.
 *
 * Characters the field cannot carry are dropped rather than flagged: an error
 * message for a key the seller did not mean to press is noise, and eBay would
 * have refused the value anyway.
 */
function LocField({ label, field, value, onChange, placeholder, error }) {
  return (
    <div>
      <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6, color:'var(--text2)' }}>
        {label}
      </label>
      <input
        value={value}
        onChange={e => onChange(clean(field, e.target.value))}
        placeholder={placeholder}
        maxLength={FIELDS[field]?.max}
        aria-invalid={!!error}
        style={{ width:'100%', padding:'11px 12px', borderRadius:12, background:'var(--bg)',
                 border:`1px solid ${error ? 'var(--red, #e53238)' : 'var(--border)'}`,
                 color:'var(--text)', fontSize:14 }}
      />
      {error && <div style={{ fontSize:11, color:'var(--red, #e53238)', marginTop:4 }}>{error}</div>}
    </div>
  )
}

export default function OnboardingFlow({ startStep = 0, shop = '', onComplete }) {
  const [step,        setStep]       = useState(startStep)
  const [savingSource, setSavingSource] = useState(null)
  const [sourceError,  setSourceError]  = useState(null)
  const [importing,    setImporting]    = useState(false)
  const [imported,     setImported]     = useState(null)
  // Which side they chose, held so the plan step can show only the cards
  // that apply. Set when the source is saved, not read back from the
  // server: the next step needs it immediately.
  const [source,      setSource]     = useState(null)
  // eBay-source only: how many listings to bring across.
  const [qty,         setQty]        = useState(1000)
  const [subscribing, setSubscribing] = useState(null)
  // Why Subscribe did nothing, when it does nothing.
  //
  // authedFetch returns the Response and does not throw on a non-2xx, so a
  // refused charge left `data.confirmationUrl` undefined, the `if` below was
  // simply false, and the button stopped spinning with nothing said. The catch
  // never ran either, so even the console stayed empty. A merchant — or a
  // reviewer — pressed the most important button in onboarding and watched an
  // unchanged screen. The 403 that caused it is fixed, but a button whose
  // failure is invisible is the condition that made it hard to find.
  const [planError,   setPlanError]   = useState('')
  const [billing,     setBilling]    = useState(null)  // { isActive, planName, trialEndsAt }
  const [loadingBill, setLoadingBill] = useState(false)

  // Step 2 — marketplace + eBay merchant location
  const [marketplaces,  setMarketplaces]  = useState([])
  const [existingLocs,  setExistingLocs]  = useState([])
  const [locMarketplace, setLocMarketplace] = useState('EBAY_US')
  const [locKey,    setLocKey]    = useState('')
  const [locName,   setLocName]   = useState('')
  const [locPostal, setLocPostal] = useState('')
  const [locCity,   setLocCity]   = useState('')
  // Only a postcode and a city are asked for here. eBay takes a state and a
  // country too, but it defaults the country from the marketplace and neither is
  // needed to create the location — so they are left to Settings → Item
  // location, where the address can be completed or corrected at any time.
  const [locFieldErrors, setLocFieldErrors] = useState({})
  // Recomputed rather than stored: the list arrives after the first render.
  const savedProblem = locKey
    ? checkSavedLocation(existingLocs.find(l => l.key === locKey))
    : null
  const [savingLoc, setSavingLoc] = useState(false)
  const [locError,  setLocError]  = useState('')
  const [connecting, setConnecting] = useState(false)
  const [ebayError,  setEbayError]  = useState('')
  // The eBay account that was just connected, so the next screen can name it.
  // Asking a seller for an inventory location without saying whose account it
  // is for leaves them guessing whether the connection worked at all — and a
  // seller with more than one eBay account cannot tell which one they linked.
  const [ebayAccount, setEbayAccount] = useState('')

  // Step 0 fallback — the app opened outside Shopify with no shop context.
  //
  // There is deliberately no "enter your store domain" field here. App Store
  // requirement 2.3.1 forbids asking for a myshopify.com domain anywhere in
  // the install or configuration flow: an install has to start on a Shopify
  // surface, which is the listing or the Admin. So the only thing this state
  // can offer is the listing itself, and the shop is identified by the
  // session token thereafter.
  const LISTING_URL = 'https://apps.shopify.com/synce-bridge'

  // Empty string → same-origin relative URL, which is what the embedded app
  // needs (it is served by the backend itself). A localhost fallback here sends
  // the Shopify iframe to the merchant's own machine, which cannot work.
  const API_URL = import.meta.env.VITE_API_URL || ''

  // When reaching step 2, check existing subscription
  useEffect(() => {
    if (step !== 4 || !shop) return
    setLoadingBill(true)
    authedFetch(`/api/billing/status?shop=${shop}`)
      .then(r => r.json())
      .then(d => {
        setBilling(d)
        // Carried past only while the subscription is genuinely still running:
        // a live charge, or a trial that has not run out. Reinstalling with time
        // left should not mean paying twice; a plan that has ended has to be
        // taken again, which is the case where a way past this step would hand
        // out the app for nothing.
        //
        // There is no button here for the same reason. One that advances the
        // plan step can be pressed, whatever it says and whatever guards it —
        // having nothing to press is the only version with no way round it.
        const stillRunning = !!d?.isActive
          || (!!d?.onTrial && (!d?.trialEndsAt || new Date(d.trialEndsAt) > new Date()))
        // Already paying, so there is nothing to choose: import and go.
        // Before the swap this advanced to the source step; the source has
        // now already been answered, so this is the end of the flow.
        if (stillRunning) finishOnboarding()
      })
      .catch(() => setBilling(null))
      .finally(() => setLoadingBill(false))
  }, [step, shop])

  // Result from the eBay consent window. Advancing here rather than on window
  // close, because the merchant may close it manually without authorising.
  //
  // Three ways in, because window.opener is not dependable: eBay's consent flow
  // is several redirects long and can end with the consent window having no
  // opener at all — a browser applying noopener, a popup that became a tab, a
  // sign-up that walked through eBay's own pages. When that happened the
  // callback used to redirect itself to the Shopify apps list, and connecting
  // eBay ended up on a different page instead of back here.
  //
  //  1. postMessage, where there is an opener.
  //  2. a storage event, which the callback writes and which reaches this
  //     window even inside its iframe — same origin — and needs no opener.
  //  3. polling the status endpoint while the window is open, which catches
  //     the case where neither of the above arrives.
  useEffect(() => {
    function accept(d) {
      if (!d || d.source !== 'syncebridge-ebay-oauth') return
      setConnecting(false)
      if (d.ok) {
        setEbayError('')
        setStep(2)               // straight to the location step
      } else {
        setEbayError(d.error || 'eBay connection failed — please try again')
      }
    }

    function onMessage(e) { accept(e.data) }

    function onStorage(e) {
      if (e.key !== 'syncebridge_ebay_oauth' || !e.newValue) return
      try { accept(JSON.parse(e.newValue)) } catch { /* not ours */ }
    }

    window.addEventListener('message', onMessage)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('message', onMessage)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  // The backstop: while the consent window is open, ask whether the connection
  // has appeared. It is the only route that works when the consent window can
  // neither reach us nor write for us, and it costs one small request every two
  // seconds for as long as the seller is actually waiting.
  useEffect(() => {
    if (!connecting || !shop) return
    let alive = true
    const id = setInterval(async () => {
      try {
        const d = await authedFetch(`/api/ebay-oauth/status?shop=${shop}`).then(r => r.json())
        if (alive && d?.connected) {
          setConnecting(false)
          setEbayError('')
          // The status call already carries the account name; it was being
          // thrown away.
          setEbayAccount(d.shopName || '')
          setStep(2)
        }
      } catch { /* keep waiting */ }
    }, 2000)
    // Given up on after five minutes rather than polling for the rest of the
    // session: the seller has abandoned the consent window by then.
    const stop = setTimeout(() => { if (alive) setConnecting(false) }, 5 * 60 * 1000)
    return () => { alive = false; clearInterval(id); clearTimeout(stop) }
  }, [connecting, shop])

  // And for the seller who arrives on the location step directly. App.jsx
  // starts them here whenever eBay is already connected, so the poll above
  // never ran and the name it would have captured was never asked for.
  useEffect(() => {
    if (step !== 2 || ebayAccount || !shop) return
    let alive = true
    authedFetch(`/api/ebay-oauth/status?shop=${shop}`)
      .then(r => r.json())
      .then(d => { if (alive && d?.connected && d.shopName) setEbayAccount(d.shopName) })
      // Silent: the name is a reassurance, and failing to fetch it is not
      // something to stop a seller with.
      .catch(() => {})
    return () => { alive = false }
  }, [step, ebayAccount, shop])

  // Load marketplaces and any locations the seller already has on eBay
  useEffect(() => {
    if (step !== 2) return
    authedFetch('/api/settings/ebay/marketplaces')
      .then(r => r.json())
      .then(d => setMarketplaces(d.marketplaces || []))
      .catch(() => setMarketplaces([]))

    if (!shop) return
    authedFetch(`/api/settings/ebay/locations?shop=${shop}`)
      .then(r => r.json())
      .then(d => {
        if (d.marketplaceId) setLocMarketplace(d.marketplaceId)
        setExistingLocs(d.locations || [])
        if (d.selectedKey) setLocKey(d.selectedKey)
      })
      .catch(() => setExistingLocs([]))
  }, [step, shop])

  async function saveLocation() {
    setLocError('')
    setLocFieldErrors({})
    // A saved location gets the same rules as one typed here. Its address is
    // what every listing will carry, and one holding a stray character was
    // accepted and then refused at publish time, naming no field.
    if (locKey) {
      const problem = checkSavedLocation(existingLocs.find(l => l.key === locKey))
      if (problem) { setLocError(`${problem} Choose another, or create a new one.`); return }
    }
    if (!locKey) {
      const errors = checkAll(
        { postalCode: locPostal, city: locCity },
        { required: ['postalCode'] })
      if (Object.keys(errors).length) { setLocFieldErrors(errors); return }
    }
    setSavingLoc(true)
    try {
      const res = await authedFetch(`/api/settings/ebay/location?shop=${shop}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          marketplaceId: locMarketplace,
          locationKey:   locKey || undefined,
          name:          locName || undefined,
          postalCode:      locPostal || undefined,
          city:            locCity || undefined,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Could not save the location')
      setStep(3)
    } catch (e) {
      setLocError(e.message)
    } finally {
      setSavingLoc(false)
    }
  }

  // eBay consent runs in a second window rather than navigating the Shopify
  // iframe away: the merchant keeps their place in the app, and the callback
  // posts the result back here before closing itself.
  function connecteBay() {
    const host = getHost()
    const params = new URLSearchParams()
    if (shop) params.set('shop', encodeURIComponent(shop))
    if (host) params.set('host', host)
    const url = `${API_URL}/api/ebay-oauth/authorize?${params.toString()}`

    const w = 560, h = 720
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2)
    const top  = window.screenY + Math.max(0, (window.outerHeight - h) / 2)
    const popup = window.open(
      url, 'ebay-oauth',
      `width=${w},height=${h},left=${Math.round(left)},top=${Math.round(top)},resizable=yes,scrollbars=yes`
    )

    // Popup blocked (common inside an iframe) — fall back to the top-level
    // redirect so the flow still completes.
    if (!popup || popup.closed) {
      redirectExternal(url)
      return
    }
    setConnecting(true)
    popup.focus()
  }

  /**
   * Save which side is the hub, then finish.
   *
   * A 409 means it was already answered, which is what an existing shop gets —
   * they are locked to what they have. Nothing to correct, so carry on into the
   * app rather than showing an error for a question that was not theirs.
   */
  async function chooseSource(source) {
    setSavingSource(source)
    try {
      const res = await authedFetch(`/api/settings/sync-source?shop=${shop}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ syncSource: source }),
      })
      if (!res.ok && res.status !== 409) {
        const d = await res.json().catch(() => ({}))
        setSourceError(d.error || 'Could not save that — please try again.')
        return
      }

      // On to the plan, which is now the last step.
      //
      // The catalogue import does NOT happen here any more. It trims itself
      // to the plan — "takes what they have paid for and no more" — so
      // running it before a plan exists imported against the entry tier's
      // limit whatever the seller went on to buy.
      setSource(source)
      setStep(4)
    } catch {
      setSourceError('Could not reach the server — please try again.')
    } finally {
      setSavingSource(null)
    }
  }
  /**
   * Bring the catalogue in, then hand over the app.
   *
   * Runs once a plan is active, because the import trims itself to the
   * plan's product limit. Finishing on an empty Products page would leave
   * the seller to work out that importing is a thing and where the button
   * is; they have just told us which side they work from, so the products
   * are the obvious next thing.
   *
   * A failure does not hold them: the catalogue is one button away inside
   * the app, and being stuck on a setup screen over it would be worse.
   */
  async function finishOnboarding() {
    setImporting(true)
    try {
      const r = await authedFetch(`/api/products/import?shop=${shop}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),        // no filters — everything, any status
      })
      const d = await r.json().catch(() => ({}))
      if (r.ok) setImported(d.imported ?? 0)
    } catch { /* reported by the app once they are in */ }
    setImporting(false)
    onComplete({ shopDomain: shop })
  }

  async function subscribe(planId) {
    setSubscribing(planId)
    try {
      const res  = await authedFetch(`/api/billing/subscribe?shop=${shop}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Monthly only. The yearly column is gone from every plan a
        // seller can choose, and this screen was still sending one.
        body: JSON.stringify({ planId, interval: 'monthly',
                               ...(planId === 'ebay_source' ? { products: Number(qty) } : {}) }),
      })
      const data = await res.json().catch(() => ({}))
      if (data.confirmationUrl) {
        // The top window: Shopify's approval page refuses to be framed, so
        // navigating this iframe to it shows a broken page with the charge
        // already raised. See utils/shopifyBridge.
        redirectExternal(data.confirmationUrl)
        return
      }
      // Anything else is a refusal, and must be visible.
      setPlanError(data.error
        || `Shopify would not start the subscription (${res.status}). `
           + `Please try again, or contact support if it keeps happening.`)
    } catch (e) {
      setPlanError(e.message || 'Could not reach the server. Please try again.')
    } finally {
      setSubscribing(null)
    }
  }

  // ── Shared layout wrapper ─────────────────────────────────────
  // Source before plan. Which side the seller works from decides which
  // plans even apply to them, so asking for money first meant showing an
  // eBay-source seller a Shopify-source ladder and vice versa.
  // The steps the seller actually works through, which does not include
  // installing the app — that already happened, and it happened in Shopify
  // rather than here. Counting it made the first screen a seller sees after
  // an install say "Step 2 of 5", because the welcome screen it was counting
  // as step 1 is skipped for a shop that is already connected. Four things
  // are asked for, so it is four steps, and the welcome screen is an
  // introduction rather than one of them.
  const steps = ['eBay', 'Location', 'Source', 'Plan']

  return (
    <div style={{
      // 100dvh (not 100vh) so mobile browser chrome and the Shopify Admin
      // iframe do not push the CTA below the fold.
      height: '100dvh',
      minHeight: '100dvh',
      background: 'var(--bg)',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* BG blobs */}
      <div style={{ position:'absolute', top:'-15%', left:'30%', width:700, height:400,
        background:'radial-gradient(ellipse, rgba(0,100,210,0.07) 0%, transparent 65%)', pointerEvents:'none' }}/>
      <div style={{ position:'absolute', bottom:'0', right:'-5%', width:400, height:400,
        background:'radial-gradient(ellipse, rgba(79,142,247,0.06) 0%, transparent 65%)', pointerEvents:'none' }}/>

      {/* Topbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'clamp(10px, 2vh, 20px) 32px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg2)',
        zIndex: 1, flexShrink: 0,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{
            width:36, height:36, borderRadius:10,
            background:'linear-gradient(135deg, var(--sh), var(--eb))',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:12, fontWeight:800, color:'#fff',
            boxShadow:'0 2px 10px rgba(0,100,210,0.2)',
          }}>S↔E</div>
          <span style={{ fontSize:16, fontWeight:700, letterSpacing:'-.3px' }}>SyncE Bridge</span>
        </div>

        {/* Shopify connection badge — reflects the real state, not a constant */}
        <div style={{
          display:'flex', alignItems:'center', gap:6,
          padding:'6px 14px',
          background: shop ? 'rgba(52,201,126,0.08)' : 'rgba(255,170,0,0.1)',
          border: shop ? '1px solid rgba(52,201,126,0.25)' : '1px solid rgba(255,170,0,0.3)',
          borderRadius:20,
          fontSize:12, fontWeight:600, color: shop ? 'var(--green)' : '#b8860b',
        }}>
          <span aria-hidden="true">{shop ? '✓' : '!'}</span>
          <span>{shop ? 'Shopify connected' : 'Not connected'}</span>
        </div>
      </div>

      {/* Main */}
      {/* Centred when the step fits, scrolled from the top when it does not.
          alignItems:center on a scrolling flex container clips the overflow
          above the fold and puts it out of reach — no scrollbar reaches it,
          because the overflow is in the negative direction. Auto margins on the
          child centre the same way and give that space back when it is needed,
          which is what a short frame or a tall step actually needs. */}
      <div style={{
        flex:1, display:'flex', flexDirection:'column',
        padding: 'clamp(12px, 3vh, 32px) 16px',
        zIndex:1,
        minHeight:0, overflowY:'auto', overflowX:'hidden',
      }}>
        <div style={{ width:'100%', maxWidth: step === 3 ? 720 : 560, margin:'auto' }}>

          {/* Step pill. Absent on the welcome screen, which is not one of
              the steps — see the steps array. */}
          {step > 0 && (
            <div style={{
              display:'inline-flex', alignItems:'center', gap:6,
              background: 'linear-gradient(135deg, rgba(0,100,210,0.15), rgba(0,100,210,0.05))',
              border:'1px solid rgba(0,100,210,0.2)',
              borderRadius:20, padding:'5px 14px',
              marginBottom:20,
            }}>
              <span style={{ fontSize:11, fontWeight:800, color:'var(--eb)', textTransform:'uppercase', letterSpacing:'.08em' }}>
                Step {step} of {steps.length}
              </span>
            </div>
          )}

          {/* ── Step 0: Shopify done ── */}
          {step === 0 && <>
            <div style={{ marginBottom:'clamp(14px, 3vh, 32px)' }}>
              <h1 style={{ fontSize:'clamp(26px, 4.6vh, 48px)', fontWeight:900, lineHeight:1.1, margin:'0 0 clamp(8px, 1.6vh, 16px)', letterSpacing:'-1.5px' }}>
                Welcome to{' '}
                <span style={{ background:'linear-gradient(135deg, var(--sh), var(--accent))', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                  SyncE Bridge
                </span>
              </h1>
              <p style={{ fontSize:16, color:'var(--text2)', margin:0, lineHeight:1.6 }}>
                {shop
                  ? <>Your Shopify store is connected. <span style={{ color:'var(--eb)', fontWeight:700 }}>eBay is next.</span></>
                  : <>This app runs inside Shopify admin. <span style={{ color:'var(--eb)', fontWeight:700 }}>Install it on your store to begin.</span></>}
              </p>
            </div>

            {/* Without a shop the app cannot do anything, so say so plainly
                rather than showing a CONNECTED badge that is not true. */}
            <div style={{
              background:'var(--bg2)', border:'1px solid var(--border)',
              borderRadius:20, padding:'clamp(14px, 2.4vh, 24px) 28px', marginBottom:'clamp(12px, 2.4vh, 24px)',
              display:'flex', alignItems:'center', gap:16,
            }}>
              <div style={{
                width:48, height:48, borderRadius:14, flexShrink:0,
                background: shop ? 'rgba(121,193,66,0.12)' : 'rgba(255,170,0,0.12)',
                border: shop ? '1px solid rgba(121,193,66,0.25)' : '1px solid rgba(255,170,0,0.3)',
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:24,
              }}>{shop ? '⬡' : '!'}</div>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:15, fontWeight:700, marginBottom:3 }}>
                  {shop ? 'Shopify store ready' : 'No Shopify store connected'}
                </div>
                <div style={{ fontSize:13, color:'var(--text2)' }}>
                  {shop || 'Open SyncE Bridge from your Shopify Admin, or install it from the Shopify App Store'}
                </div>
              </div>
              <div style={{ marginLeft:'auto', fontSize:11, fontWeight:700, flexShrink:0,
                color: shop ? 'var(--green)' : '#b8860b',
                background: shop ? 'rgba(52,201,126,0.1)' : 'rgba(255,170,0,0.12)',
                border: shop ? '1px solid rgba(52,201,126,0.2)' : '1px solid rgba(255,170,0,0.3)',
                borderRadius:20, padding:'4px 12px' }}>
                {shop ? 'CONNECTED' : 'NOT CONNECTED'}
              </div>
            </div>

            {!shop && (
              <p style={{ fontSize:13, color:'var(--text2)', lineHeight:1.6,
                          margin:'0 0 clamp(12px, 2.4vh, 24px)' }}>
                SyncE Bridge is installed from the Shopify App Store and runs inside your
                Shopify Admin, which is where it reads which store it is working for. If the
                app is already installed, open it from <strong>Apps</strong> in your Shopify
                Admin.
              </p>
            )}

            {/* With a shop this moves to the eBay step; without one the only
                place to send the seller is the App Store listing, since the
                install has to begin on a Shopify surface. Rendered as a link
                rather than a button so it opens the listing at the top level
                and not inside whatever frame this page is in. */}
            {shop ? (
              <button onClick={() => setStep(1)} style={{
                width:'100%', padding:'16px',
                background:'linear-gradient(135deg, #0064d2, #e53238)',
                border:'none', borderRadius:14,
                color:'#fff', fontSize:16, fontWeight:800, cursor:'pointer',
                boxShadow:'0 4px 20px rgba(0,100,210,0.35)',
                transition:'transform .15s, box-shadow .15s',
                letterSpacing:'-.2px',
              }}
                onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 28px rgba(0,100,210,0.45)' }}
                onMouseLeave={e=>{ e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 4px 20px rgba(0,100,210,0.35)' }}
              >
                {"let's connect ebay →"}
              </button>
            ) : (
              <a href={LISTING_URL} target="_top" rel="noopener" style={{
                display:'block', width:'100%', padding:'16px', boxSizing:'border-box',
                background:'linear-gradient(135deg, #0064d2, #e53238)',
                border:'none', borderRadius:14, textAlign:'center', textDecoration:'none',
                color:'#fff', fontSize:16, fontWeight:800, cursor:'pointer',
                boxShadow:'0 4px 20px rgba(0,100,210,0.35)',
                transition:'transform .15s, box-shadow .15s',
                letterSpacing:'-.2px',
              }}
                onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 28px rgba(0,100,210,0.45)' }}
                onMouseLeave={e=>{ e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 4px 20px rgba(0,100,210,0.35)' }}
              >
                {'install from the shopify app store →'}
              </a>
            )}
          </>}

          {/* ── Step 1: Connect eBay ── */}
          {step === 1 && <>
            <div style={{ marginBottom:'clamp(14px, 3vh, 32px)' }}>
              <h1 style={{ fontSize:'clamp(26px, 4.6vh, 48px)', fontWeight:900, lineHeight:1.1, margin:'0 0 clamp(8px, 1.6vh, 16px)', letterSpacing:'-1.5px' }}>
                Connect{' '}
                <span style={{ background:'linear-gradient(135deg, #0064d2, #e53238)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                  eBay
                </span>
              </h1>
              <p style={{ fontSize:16, color:'var(--text2)', margin:0, lineHeight:1.6 }}>
                Link your eBay seller account and we'll keep your listings{' '}
                <span style={{ background:'linear-gradient(135deg, #e53238, #0064d2)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', fontWeight:700 }}>
                  perfectly in sync
                </span>
                {' '}with Shopify.
              </p>
            </div>

            {/* Permissions */}
            <div style={{ marginBottom:28 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'.1em', marginBottom:14 }}>
                Permissions we'll request
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {PERMS.map((p, i) => (
                  <div key={i} style={{
                    display:'flex', alignItems:'center', gap:14,
                    background:'var(--bg2)', border:'1px solid var(--border)',
                    borderRadius:14, padding:'14px 18px',
                    transition:'border-color .15s, background .15s',
                  }}
                    onMouseEnter={e=>{ e.currentTarget.style.borderColor='var(--border2)'; e.currentTarget.style.background='var(--bg3)' }}
                    onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.background='var(--bg2)' }}
                  >
                    <div aria-hidden="true" style={{
                      width:40, height:40, borderRadius:10, flexShrink:0,
                      background:'var(--bg3)', border:'1px solid var(--border)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:17, color:'var(--eb)',
                    }}>{p.mark}</div>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, marginBottom:2 }}>{p.title}</div>
                      <div style={{ fontSize:12, color:'var(--text3)' }}>{p.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={connecteBay} style={{
              width:'100%', padding:'16px',
              background:'linear-gradient(135deg, #0064d2, #e53238)',
              border:'none', borderRadius:14,
              color:'#fff', fontSize:16, fontWeight:800, cursor:'pointer',
              boxShadow:'0 4px 20px rgba(0,100,210,0.35)',
              transition:'transform .15s, box-shadow .15s',
              marginBottom:12, letterSpacing:'-.2px',
            }}
              onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 28px rgba(0,100,210,0.5)' }}
              onMouseLeave={e=>{ e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 4px 20px rgba(0,100,210,0.35)' }}
            >
              {connecting ? 'Waiting for eBay…' : 'Connect eBay account'}
            </button>

            {connecting && (
              <div style={{ fontSize:12, color:'var(--text3)', textAlign:'center', marginBottom:8 }}>
                Finish signing in to eBay in the window that opened. It closes on its own.
              </div>
            )}
            {ebayError && (
              <div style={{ fontSize:13, color:'var(--red, #e53238)', textAlign:'center', marginBottom:8 }}>
                {ebayError}
              </div>
            )}

          </>}

          {/* ── Step 2: Marketplace + inventory location ──
              eBay rejects an offer publish unless the seller has a merchant
              location, and the marketplace fixes the listing country and
              currency. Collected here so the first publish cannot fail on it. */}
          {step === 2 && <>
            <div style={{ marginBottom:'clamp(14px, 3vh, 32px)' }}>
              <h1 style={{ fontSize:'clamp(26px, 4.6vh, 48px)', fontWeight:900, lineHeight:1.1, margin:'0 0 clamp(8px, 1.6vh, 16px)', letterSpacing:'-1.5px' }}>
                Where do you{' '}
                <span style={{ background:'linear-gradient(135deg, #0064d2, #e53238)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                  ship from?
                </span>
              </h1>
              <p style={{ fontSize:16, color:'var(--text2)', margin:0, lineHeight:1.6 }}>
                eBay needs a marketplace and an inventory location before it will publish a listing.
              </p>

              {/* Whose account this is for. Without it the seller is asked
                  for a shipping address with nothing on screen confirming
                  the connection they just made worked, and someone with more
                  than one eBay account cannot tell which one they linked. */}
              {ebayAccount && (
                <div style={{
                  display:'inline-flex', alignItems:'center', gap:8, marginTop:14,
                  padding:'6px 12px', borderRadius:999,
                  background:'var(--bg2)', border:'1px solid var(--border)',
                }}>
                  <span style={{ width:7, height:7, borderRadius:'50%',
                                 background:'var(--green, #2e7d32)', flexShrink:0 }} />
                  <span style={{ fontSize:12.5, color:'var(--text2)' }}>
                    Connected to eBay as{' '}
                    <strong style={{ color:'var(--text)' }}>{ebayAccount}</strong>
                  </span>
                </div>
              )}
            </div>

            <div style={{
              background:'var(--bg2)', border:'1px solid var(--border)',
              borderRadius:20, padding:'clamp(14px, 2.4vh, 24px) 28px',
              marginBottom:'clamp(12px, 2.4vh, 24px)',
            }}>
              <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6, color:'var(--text2)' }}>
                eBay marketplace
              </label>
              <select
                value={locMarketplace}
                onChange={e => setLocMarketplace(e.target.value)}
                style={{
                  width:'100%', padding:'11px 12px', borderRadius:12, marginBottom:14,
                  border:'1px solid var(--border)', background:'var(--bg)', color:'var(--text)', fontSize:14,
                }}
              >
                {marketplaces.map(m => (
                  <option key={m.id} value={m.id}>{m.name} — {m.currency}</option>
                ))}
              </select>

              {existingLocs.length > 0 && (
                <>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6, color:'var(--text2)' }}>
                    Use an existing eBay location
                  </label>
                  <select
                    value={locKey}
                    onChange={e => { setLocKey(e.target.value); setLocError('') }}
                    style={{
                      width:'100%', padding:'11px 12px', borderRadius:12, marginBottom:6,
                      border:`1px solid ${savedProblem ? 'var(--red, #e53238)' : 'var(--border)'}`,
                      background:'var(--bg)', color:'var(--text)', fontSize:14,
                    }}
                  >
                    <option value="">➕ Create a new location</option>
                    {existingLocs.map(l => {
                      // Marked in the list itself, so an unusable one is obvious
                      // before it is chosen rather than after Save is pressed.
                      const bad = checkSavedLocation(l)
                      return (
                        <option key={l.key} value={l.key}>
                          {bad ? '⚠ ' : ''}{l.name || l.key} {l.postal ? `— ${l.postal}` : ''} {l.country ? `(${l.country})` : ''}
                        </option>
                      )
                    })}
                  </select>
                  <div style={{ marginBottom:14, fontSize:11,
                                color: savedProblem ? 'var(--red, #e53238)' : 'var(--text3)' }}>
                    {savedProblem
                      ? `${savedProblem} Choose another, or create a new one.`
                      : 'A location marked ⚠ holds something eBay will not accept.'}
                  </div>
                </>
              )}

              {!locKey && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div style={{ gridColumn:'1 / -1' }}>
                    <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6, color:'var(--text2)' }}>
                      Location name
                    </label>
                    <input
                      value={locName} onChange={e => setLocName(e.target.value)}
                      placeholder="Primary warehouse"
                      style={{ width:'100%', padding:'11px 12px', borderRadius:12, border:'1px solid var(--border)', background:'var(--bg)', color:'var(--text)', fontSize:14 }}
                    />
                  </div>
                  {/* Cleaned as it is typed rather than rejected afterwards: a
                      stray quote in a postcode is refused by eBay at publish
                      time, in terms that name no field. See lib/validate.js. */}
                  <LocField label="Postal / ZIP code *" field="postalCode" value={locPostal}
                    onChange={setLocPostal} placeholder="95125" error={locFieldErrors.postalCode}/>
                  <LocField label="City" field="city" value={locCity}
                    onChange={setLocCity} placeholder="San Jose" error={locFieldErrors.city}/>
                </div>
              )}

              {locError && (
                <div style={{ marginTop:12, fontSize:13, color:'var(--red, #e53238)' }}>{locError}</div>
              )}
            </div>

            <button onClick={saveLocation} disabled={savingLoc} style={{
              width:'100%', padding:'16px',
              background:'linear-gradient(135deg, #0064d2, #e53238)',
              border:'none', borderRadius:14,
              color:'#fff', fontSize:16, fontWeight:800,
              cursor: savingLoc ? 'wait' : 'pointer',
              opacity: savingLoc ? 0.7 : 1,
              boxShadow:'0 4px 20px rgba(0,100,210,0.35)',
              marginBottom:12, letterSpacing:'-.2px',
            }}>
              {savingLoc ? 'SAVING…' : 'SAVE AND CONTINUE →'}
            </button>

          </>}

          {/* ── Step 3: Pricing ── */}
          {step === 4 && <>
            <div style={{ marginBottom:'clamp(14px, 3vh, 32px)' }}>
              <h1 style={{ fontSize:44, fontWeight:900, lineHeight:1.1, margin:'0 0 16px', letterSpacing:'-1.5px' }}>
                Choose your{' '}
                <span style={{ background:'linear-gradient(135deg, var(--accent), var(--purple))', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                  plan
                </span>
              </h1>
              <p style={{ fontSize:15, color:'var(--text2)', margin:0, lineHeight:1.6 }}>
                Every plan starts with a 7-day free trial. Cancel any time.
              </p>
            </div>

            {/* Already subscribed banner */}
            {/* No "already subscribed" panel, and no button beside it. A seller
                with an active plan never reaches this screen — the effect above
                moves them straight on. */}

            {/* Only the cards for the side they chose.
                Showing both meant an eBay-source seller reading a
                Shopify-source ladder, and the source is already answered by
                the time this renders. The yearly toggle that used to sit
                here is gone: there is no yearly price to choose. */}
            {source === 'ebay' ? (() => {
              const n    = Math.min(EBAY_MAX_PRODUCTS,
                             Math.max(EBAY_MIN_PRODUCTS, Math.floor(Number(qty) || 0)))
              const tier = tierFor(n)
              const fee  = setupFee(n)
              const money = v => v.toLocaleString(undefined, { minimumFractionDigits: 2 })
              return (
                <div style={{ marginBottom:20 }}>
                  <label htmlFor="ob-qty" style={{ display:'block', fontSize:13, fontWeight:700, marginBottom:6 }}>
                    How many eBay listings do you want brought into Shopify?
                  </label>
                  <input id="ob-qty" type="number" inputMode="numeric"
                    min={EBAY_MIN_PRODUCTS} max={EBAY_MAX_PRODUCTS} step={100}
                    value={qty}
                    onChange={e => setQty(e.target.value)}
                    onBlur={() => setQty(Math.min(EBAY_MAX_PRODUCTS,
                                         Math.max(EBAY_MIN_PRODUCTS, Math.floor(Number(qty) || 0))))}
                    style={{ width:'100%', maxWidth:300, padding:'11px 12px', borderRadius:12,
                             background:'var(--bg)', border:'1px solid var(--border)',
                             color:'var(--text)', fontSize:15, fontFamily:'var(--mono)',
                             marginBottom:6 }}/>
                  <div style={{ fontSize:11.5, color:'var(--text3)', marginBottom:18 }}>
                    {EBAY_SETUP_PER_PRODUCT * 100}&cent; per listing, charged once &middot;
                    minimum {EBAY_MIN_PRODUCTS.toLocaleString()}
                  </div>

                  <div style={{ background:'var(--bg2)', border:'1px solid rgba(79,142,247,0.35)',
                                borderRadius:16, padding:20, marginBottom:12 }}>
                    <div style={{ fontSize:14, fontWeight:700, marginBottom:10 }}>
                      Import + {tier.name}
                    </div>
                    <div style={{ marginBottom:6 }}>
                      <span style={{ fontSize:28, fontWeight:900, letterSpacing:'-1px' }}>${tier.monthly}</span>
                      <span style={{ fontSize:12, color:'var(--text3)', marginLeft:3 }}>/mo</span>
                      <span style={{ fontSize:12, color:'var(--text2)', marginLeft:8 }}>
                        plus ${money(fee)} once
                      </span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--text2)', marginBottom:14, lineHeight:1.5 }}>
                      {n.toLocaleString()} listings brought across, then{' '}
                      {tier.products.toLocaleString()} products kept in step.
                    </div>
                    <button onClick={() => subscribe('ebay_source')}
                      disabled={!!subscribing}
                      style={{ width:'100%', padding:'10px', borderRadius:10, border:'none',
                               background:'linear-gradient(135deg, var(--accent), var(--accent2))',
                               color:'#fff', fontSize:13, fontWeight:700,
                               cursor: subscribing ? 'not-allowed' : 'pointer' }}>
                      {subscribing === 'ebay_source' ? 'Starting…'
                        : `Start — $${money(fee)} then $${tier.monthly}/mo`}
                    </button>
                  </div>
                  <div style={{ fontSize:11.5, color:'var(--text3)', lineHeight:1.6 }}>
                    The one-time fee creates your Shopify products and ends there.
                    The monthly plan is what keeps stock and price in step.
                  </div>
                </div>
              )
            })() : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:12, marginBottom:20 }}>
              {SHOPIFY_PLANS.map(plan => (
                <div key={plan.id} style={{
                  background: plan.popular ? 'linear-gradient(135deg, rgba(79,142,247,0.1), rgba(79,142,247,0.04))' : 'var(--bg2)',
                  border: plan.popular ? '1px solid rgba(79,142,247,0.35)' : '1px solid var(--border)',
                  borderRadius:16, padding:'20px',
                  position:'relative',
                  transition:'transform .15s, box-shadow .15s',
                  cursor:'pointer',
                }}
                  onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.2)' }}
                  onMouseLeave={e=>{ e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none' }}
                >
                  {plan.popular && (
                    <div style={{
                      position:'absolute', top:-10, left:'50%', transform:'translateX(-50%)',
                      background:'linear-gradient(135deg, var(--accent), var(--accent2))',
                      borderRadius:20, padding:'3px 12px',
                      fontSize:10, fontWeight:800, color:'#fff', whiteSpace:'nowrap',
                      letterSpacing:'.05em',
                    }}>MOST POPULAR</div>
                  )}
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                    <span style={{ fontSize:14, fontWeight:700 }}>{plan.name}</span>
                  </div>
                  <div style={{ marginBottom:8 }}>
                    <span style={{ fontSize:28, fontWeight:900, letterSpacing:'-1px' }}>
                      ${plan.monthly}
                    </span>
                    <span style={{ fontSize:12, color:'var(--text3)', marginLeft:3 }}>/mo</span>
                  </div>
                  <div style={{ fontSize:11.5, color:'var(--text3)', marginBottom:10 }}>
                    {plan.products.toLocaleString()} products &middot;{' '}
                    {plan.orders.toLocaleString()} orders a month
                  </div>
                  <div style={{ fontSize:12, color:'var(--text2)', marginBottom:14, lineHeight:1.4 }}>{plan.desc}</div>
                  <button
                    onClick={() => subscribe(plan.id)}
                    disabled={!!subscribing}
                    style={{
                      width:'100%', padding:'10px',
                      background: plan.popular ? 'linear-gradient(135deg, var(--accent), var(--accent2))' : 'var(--bg3)',
                      border: plan.popular ? 'none' : '1px solid var(--border)',
                      borderRadius:10, cursor: subscribing ? 'not-allowed' : 'pointer',
                      color: plan.popular ? '#fff' : 'var(--text)',
                      fontSize:13, fontWeight:700,
                      opacity: subscribing && subscribing !== plan.id ? 0.5 : 1,
                      transition:'opacity .15s',
                    }}
                  >
                    {subscribing === plan.id ? 'Starting…' : 'Start free trial'}
                  </button>
                </div>
              ))}
            </div>
            )}

            {/* Shown where the button is, because a refusal with nothing on
                screen is how a seller concludes the app is broken and leaves. */}
            {planError && (
              <div role="alert" style={{ fontSize:13, color:'var(--red, #e53238)',
                                         textAlign:'center', marginTop:10 }}>
                {planError}
              </div>
            )}

            <div style={{ textAlign:'center', fontSize:12, color:'var(--text3)', marginTop:4 }}>
              All plans include a 7-day free trial. No charge until the trial ends.
            </div>
          </>}

          {/* ── Step 3: Source to manage products ── */}
          {/* Asked once, and only of a new shop. It decides whether eBay
              listings become new Shopify products or are linked to ones that
              already exist — which is the difference between a tidy catalogue
              and a duplicated one, and cannot be guessed from the outside. */}
          {step === 3 && <>
            <div style={{ fontSize:'clamp(20px, 3.4vh, 26px)', fontWeight:750, marginBottom:6 }}>
              How your catalogue syncs
            </div>
            <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20, lineHeight:1.6 }}>
              You manage your listings on eBay and they appear in Shopify. Stock and price
              follow eBay — nothing about a product is sent from Shopify back to eBay.
            </div>

            <div style={{ display:'grid', gap:12, marginBottom:16 }}>
              {OFFERED_SOURCES.map(src => (
                <button key={src.id} onClick={()=>chooseSource(src.id)}
                  disabled={!!savingSource || importing}
                  style={{
                    textAlign:'left', padding:'18px 20px', borderRadius:12, cursor: savingSource ? 'wait' : 'pointer',
                    background:'var(--bg2)', border:'1px solid var(--border)',
                    fontFamily:'inherit', transition:'all .15s',
                    opacity: savingSource && savingSource !== src.id ? .5 : 1,
                  }}
                  onMouseEnter={e=>{ if(!savingSource) e.currentTarget.style.borderColor='var(--accent)' }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--border)' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
                    <span style={{ fontSize:15, fontWeight:700 }}>{src.title}</span>
                    {savingSource === src.id && (
                      <span style={{ fontSize:12, color:'var(--text3)', marginLeft:'auto' }}>Saving…</span>
                    )}
                  </div>
                  <div style={{ fontSize:12.5, color:'var(--text2)', lineHeight:1.6 }}>{src.sub}</div>
                  <div style={{ fontSize:11.5, color:'var(--text3)', marginTop:8, lineHeight:1.6 }}>
                    {src.detail}
                  </div>
                </button>
              ))}
            </div>

            {importing && (
              <div style={{ background:'var(--accent-bg)', border:'1px solid var(--accent)',
                            borderRadius:10, padding:'12px 16px', marginBottom:12,
                            fontSize:13, color:'var(--text2)' }}>
                Bringing your Shopify products in… this takes a moment for a large catalogue.
              </div>
            )}

            {imported !== null && !importing && (
              <div style={{ background:'var(--green-bg)', border:'1px solid var(--green)',
                            borderRadius:10, padding:'12px 16px', marginBottom:12,
                            fontSize:13, color:'var(--text2)' }}>
                {imported} product{imported === 1 ? '' : 's'} imported.
              </div>
            )}

            {sourceError && (
              <div style={{ background:'var(--amber-bg)', border:'1px solid var(--amber)',
                            borderRadius:10, padding:'10px 14px', fontSize:12, color:'var(--text2)' }}>
                {sourceError}
              </div>
            )}
          </>}

        </div>
      </div>

      {/* Footer */}
      <div style={{
        textAlign:'center', padding:'clamp(8px, 1.6vh, 16px)',
        borderTop:'1px solid var(--border)',
        fontSize:12, color:'var(--text3)',
        zIndex:1, flexShrink:0,
      }}>
        your data is encrypted &amp; secure ✨
      </div>

    </div>
  )
}
